// Vendor selectors and field policy

const FORM_SELECTOR = '.form-builder-ips';
const FIELD_ROW_SELECTOR = '.grid-item';
const SUBMISSION_FEEDBACK_SELECTOR = '.bannerCardWrapper .bannerContainer';
const STOCK_NOTIFY_SELECTOR = '[data-stock-notify]';
const SUBMIT_CONTROL_SELECTOR = 'button[type="submit"], input[type="submit"], button[id^="submit-"]';
const VENDOR_REQUIRED_INDICATOR_SELECTOR = '.ipsFormPreviewTextRequired';
const FIELD_ERROR_SELECTOR = '[data-netsuite-field-error]';
const FIELD_SELECTORS = {
  phone: '.vk-field-phone input',
  smsOptIn: '.vk-field-sms-consent input[type="checkbox"]',
  contactMethod: '.vk-field-contact-method select',
  itemName: '.vk-field-item-name input',
  itemSku: '.vk-field-item-sku input',
  surveyOrderReference: '.vk-field-survey-order-reference input',
};

const SURVEY_ORDER_ID_PATTERN = /^\d{1,50}$/;
// Formatting characters (spaces, dots, dashes, parentheses) and a trailing extension are ignored.
// Without a leading + (or 011/00 dialing prefix) the number is treated as US/Canada and needs
// 10 digits, or 11 with a leading 1. International numbers allow the E.164 range of 8-15 digits.
const PHONE_FORMAT_PATTERN = /^\+?[\d\s().-]+?(?:\s*(?:ext\.?|x|#)\s*\d{1,6})?$/i;
const PHONE_EXTENSION_PATTERN = /\s*(?:ext\.?|x|#)\s*\d{1,6}$/i;
const PHONE_INTERNATIONAL_DIALING_PREFIX = /^(?:011|00)/;
const PHONE_INTERNATIONAL_MIN_DIGITS = 8;
const PHONE_INTERNATIONAL_MAX_DIGITS = 15;
const PHONE_REQUIRED_MESSAGE = 'Please fill in field';
const PHONE_INVALID_MESSAGE = 'Please enter a valid phone number';
const PHONE_CONTACT_METHOD = 'phone';
const SUBMIT_CLICK_EVENTS = new Map([
  ['contact', 'vk_contact_form_submit_clicked'],
  ['repair_request', 'vk_repair_request_submit_clicked'],
  ['product_unavailable', 'vk_product_unavailable_submit_clicked'],
]);

// Theme markup opts each form into named behaviors while the implementation stays version controlled.
const FORM_BEHAVIORS = {
  'product-fields': {
    fields: [
      {
        selector: FIELD_SELECTORS.itemName,
        getValue: (form) => getProductContext(form).title,
        readOnly: true,
      },
      {
        selector: FIELD_SELECTORS.itemSku,
        getValue: (form) => getProductContext(form).sku,
        readOnly: false,
      },
    ],
  },
  'survey-order-reference': {
    fields: [
      {
        selector: FIELD_SELECTORS.surveyOrderReference,
        getValue: getSurveyOrderReference,
        readOnly: false,
      },
    ],
  },
};

// Vendor DOM helpers

const inputValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;

function findFieldLabel(field) {
  return Array.from(field.closest(FORM_SELECTOR)?.querySelectorAll('label') ?? []).find(
    (label) => label.htmlFor === field.id
  );
}

function setFieldValue(field, value) {
  // Native setters plus bubbling events keep the app's serializer state synchronized.
  if (inputValueSetter) {
    inputValueSetter.call(field, value);
  } else {
    field.value = value;
  }

  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

function hideTechnicalField(field) {
  field.readOnly = false;
  field.autocomplete = 'off';
  field.tabIndex = -1;
  field.setAttribute('aria-hidden', 'true');

  const row = field.closest(FIELD_ROW_SELECTOR);
  if (row instanceof HTMLElement) row.hidden = true;
}

function showReadOnlyField(field) {
  field.readOnly = true;
  field.autocomplete = 'off';
  field.removeAttribute('aria-hidden');
  field.removeAttribute('tabindex');

  const row = field.closest(FIELD_ROW_SELECTOR);
  if (row instanceof HTMLElement) row.hidden = false;
}

function syncRequiredIndicator(field, required) {
  const label = findFieldLabel(field);
  const vendorIndicator = label?.querySelector(VENDOR_REQUIRED_INDICATOR_SELECTOR);
  const themeIndicator = label?.querySelector('[data-netsuite-required-indicator]');

  if (vendorIndicator instanceof HTMLElement) {
    vendorIndicator.hidden = !required;
    themeIndicator?.remove();
    return;
  }

  if (!required) {
    themeIndicator?.remove();
    return;
  }

  if (!label || themeIndicator) return;

  const requiredIndicator = document.createElement('span');
  requiredIndicator.dataset.netsuiteRequiredIndicator = 'true';
  requiredIndicator.setAttribute('aria-hidden', 'true');
  requiredIndicator.textContent = ' *';
  label.append(requiredIndicator);
}

// Value sources

function getProductContext(form) {
  const section = form.closest('[id*="ProductInformation-"]');
  const source =
    form.closest('[data-product-title][data-product-sku]') ??
    section?.querySelector('[data-product-title][data-product-sku]');

  return {
    title: source?.dataset.productTitle ?? '',
    sku: source?.dataset.productSku ?? '',
  };
}

function getSurveyOrderReference() {
  const orderId = new URLSearchParams(window.location.search).get('soID') ?? '';
  return SURVEY_ORDER_ID_PATTERN.test(orderId) ? orderId : '';
}

function configureFieldValues(form, fields) {
  fields.forEach(({ selector, getValue, readOnly }) => {
    const field = form.querySelector(selector);
    if (!(field instanceof HTMLInputElement)) return;

    if (readOnly) {
      showReadOnlyField(field);
    } else {
      hideTechnicalField(field);
    }
    setFieldValue(field, getValue(form));
  });
}

// Phone format validation

export function isValidPhoneNumber(value) {
  const trimmed = value.trim();
  if (!PHONE_FORMAT_PATTERN.test(trimmed)) return false;

  const digits = trimmed.replace(PHONE_EXTENSION_PATTERN, '').replace(/\D/g, '');

  if (trimmed.startsWith('+') || PHONE_INTERNATIONAL_DIALING_PREFIX.test(digits)) {
    const countryAndNumber = trimmed.startsWith('+') ? digits : digits.replace(PHONE_INTERNATIONAL_DIALING_PREFIX, '');
    // +1 is the US/Canada country code, so hold it to the same 10-digit rule.
    if (countryAndNumber.startsWith('1')) return countryAndNumber.length === 11;
    return (
      countryAndNumber.length >= PHONE_INTERNATIONAL_MIN_DIGITS &&
      countryAndNumber.length <= PHONE_INTERNATIONAL_MAX_DIGITS
    );
  }

  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

// Custom validity lets checkValidity() and the vendor's own checks see a bad format.
function syncPhoneFormatValidity(phone) {
  const value = phone.value.trim();
  phone.setCustomValidity(value === '' || isValidPhoneNumber(value) ? '' : PHONE_INVALID_MESSAGE);
}

// Conditional Phone requirement (visibility is owned by the form builder).

function configurePhoneRequirement(form) {
  const phone = form.querySelector(FIELD_SELECTORS.phone);
  if (!(phone instanceof HTMLInputElement)) return;

  const smsOptIn = form.querySelector(FIELD_SELECTORS.smsOptIn);
  const contactMethod = form.querySelector(FIELD_SELECTORS.contactMethod);
  const requiresPhone =
    (smsOptIn instanceof HTMLInputElement && smsOptIn.checked) ||
    (contactMethod instanceof HTMLSelectElement && contactMethod.value.trim().toLowerCase() === PHONE_CONTACT_METHOD);

  phone.required = requiresPhone;
  // The builder only captures type="text" inputs into its submission data, so the type must not
  // change; inputmode alone brings up the phone keypad on mobile.
  phone.inputMode = 'tel';
  if (!phone.autocomplete) phone.autocomplete = 'tel';
  syncRequiredIndicator(phone, requiresPhone);
  syncPhoneFormatValidity(phone);

  if (!requiresPhone && phone.validity.valid) clearFieldError(phone);
}

function clearFieldError(field) {
  field.removeAttribute('aria-invalid');
  field.closest(FIELD_ROW_SELECTOR)?.querySelector(FIELD_ERROR_SELECTOR)?.remove();
}

function showFieldError(field, message, { focus = true } = {}) {
  field.setAttribute('aria-invalid', 'true');

  const row = field.closest(FIELD_ROW_SELECTOR);
  if (row) {
    let error = row.querySelector(FIELD_ERROR_SELECTOR);
    if (!error) {
      error = document.createElement('div');
      error.dataset.netsuiteFieldError = 'true';
      error.setAttribute('role', 'alert');
      row.append(error);
    }
    if (error.textContent !== message) error.textContent = message;
  }

  if (focus) field.focus();
}

function validatePhoneRequirement(form) {
  configurePhoneRequirement(form);

  const phone = form.querySelector(FIELD_SELECTORS.phone);
  if (!(phone instanceof HTMLInputElement)) return true;

  if (phone.checkValidity()) {
    clearFieldError(phone);
    return true;
  }

  showFieldError(phone, phone.validity.customError ? PHONE_INVALID_MESSAGE : PHONE_REQUIRED_MESSAGE);
  return false;
}

function hasRequiredIndicator(field) {
  const label = findFieldLabel(field);
  const indicator = label?.querySelector(
    `${VENDOR_REQUIRED_INDICATOR_SELECTOR}, [data-netsuite-required-indicator]`
  );

  return indicator instanceof HTMLElement && !indicator.hidden;
}

function requiredFieldsAreValid(form) {
  return Array.from(form.querySelectorAll('input, select, textarea')).every((field) => {
    if (
      (!(field instanceof HTMLInputElement) &&
        !(field instanceof HTMLSelectElement) &&
        !(field instanceof HTMLTextAreaElement)) ||
      field.disabled ||
      (!field.required && !hasRequiredIndicator(field))
    ) {
      return true;
    }

    if (field instanceof HTMLInputElement && ['checkbox', 'radio'].includes(field.type)) {
      return field.checked;
    }

    return field.value.trim() !== '' && field.checkValidity();
  });
}

function submissionHost(form) {
  return form.closest('dialog') ?? form;
}

function submissionIsPending(form) {
  return Boolean(form.dataset.netsuiteSubmitting || submissionHost(form).dataset.netsuiteSubmitting);
}

function setSubmissionPending(form, pending, manageNativeDisabled = true) {
  const host = submissionHost(form);

  if (pending) {
    form.dataset.netsuiteSubmitting = 'true';
    host.dataset.netsuiteSubmitting = 'true';
  } else {
    delete host.dataset.netsuiteSubmitExpected;
    delete form.dataset.netsuiteSubmitting;
    delete host.dataset.netsuiteSubmitting;
  }

  form.querySelectorAll(SUBMIT_CONTROL_SELECTOR).forEach((control) => {
    if (!(control instanceof HTMLButtonElement) && !(control instanceof HTMLInputElement)) return;

    if (manageNativeDisabled || !pending) control.disabled = pending;
    if (pending) {
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('aria-busy', 'true');
    } else {
      control.removeAttribute('aria-disabled');
      control.removeAttribute('aria-busy');
    }
  });
}

function presentSubmissionFeedback(feedback) {
  feedback.tabIndex = -1;
  feedback.focus({ preventScroll: true });
  feedback.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
}

function handleSubmissionFeedback(form) {
  if (!submissionIsPending(form)) return;

  const feedback = form.querySelector(SUBMISSION_FEEDBACK_SELECTOR);
  if (!(feedback instanceof HTMLElement) || getComputedStyle(feedback).display === 'none') return;

  setSubmissionPending(form, false);
  presentSubmissionFeedback(feedback);
}

function configureSubmission(form) {
  if (form.dataset.netsuiteSubmissionConfigured) return;

  const submissionForm = form.matches('form') ? form : form.querySelector('form');

  const feedbackObserver = new MutationObserver(() => handleSubmissionFeedback(form));
  feedbackObserver.observe(form, {
    attributes: true,
    attributeFilter: ['hidden', 'style'],
    characterData: true,
    childList: true,
    subtree: true,
  });

  form.addEventListener(
    'submit',
    (event) => {
      const host = submissionHost(form);
      const expectedSubmit = host.dataset.netsuiteSubmitExpected === 'true';
      delete host.dataset.netsuiteSubmitExpected;

      if (!expectedSubmit && submissionIsPending(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const feedbackClose = form.querySelector(`${SUBMISSION_FEEDBACK_SELECTOR} .bannerCloseButton`);
      if (feedbackClose instanceof HTMLElement) feedbackClose.click();

      // Refresh generated values immediately before the app serializes the payload.
      configureForm(form);

      if (!validatePhoneRequirement(form)) {
        setSubmissionPending(form, false);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      // Div-based vendor forms own their async and validation states.
      if (!(submissionForm instanceof HTMLFormElement)) return;

      if (!submissionForm.checkValidity()) {
        setSubmissionPending(form, false);
        event.preventDefault();
        event.stopImmediatePropagation();
        submissionForm.reportValidity();
        return;
      }

      setSubmissionPending(form, true);
    },
    { capture: true }
  );

  form.dataset.netsuiteSubmissionConfigured = 'true';
  handleSubmissionFeedback(form);
}

/** @param {HTMLElement} form */
function trackSubmitClick(form) {
  const formKey = form.dataset.storeFormKey;
  if (!formKey) return;
  const eventName = SUBMIT_CLICK_EVENTS.get(formKey);
  const analytics = window.Shopify?.analytics;
  if (!eventName || window.Shopify?.designMode || typeof analytics?.publish !== 'function') return;

  const data = {
    form_key: formKey,
    form_id: form.dataset.key,
    form_name: form.getAttribute('form-name'),
  };

  if (formKey === 'product_unavailable') {
    const product = form.closest('.product-unavailable');
    if (product instanceof HTMLElement) {
      Object.assign(data, {
        item_id: product.dataset.productItemId || product.dataset.productSku,
        item_name: product.dataset.productTitle,
        item_variant: product.dataset.productVariant,
      });
      const price = product.dataset.productPrice;
      if (price && Number.isFinite(Number(price))) Object.assign(data, { price: Number(price) });
    }
  }

  try {
    Promise.resolve(analytics.publish(eventName, data)).catch((error) => {
      console.warn('[Analytics] Could not publish form submit click.', error);
    });
  } catch (error) {
    console.warn('[Analytics] Could not publish form submit click.', error);
  }
}

function handleSubmitControlClick(event) {
  if (!(event.target instanceof Element)) return;

  const submitControl = event.target.closest(SUBMIT_CONTROL_SELECTOR);
  const form = submitControl?.closest(FORM_SELECTOR);
  if (!(submitControl instanceof HTMLElement) || !(form instanceof HTMLElement)) return;

  if (submissionIsPending(form)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  // Track the attempt before validation can stop propagation to other click listeners.
  if (!submitControl.matches(':disabled')) trackSubmitClick(form);

  configureForm(form);

  if (!validatePhoneRequirement(form)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  // Let the vendor render its own errors when its required fields are incomplete.
  if (!requiredFieldsAreValid(form)) return;

  // Native forms enter pending from their submit event after browser validation.
  const submissionForm = form.matches('form') ? form : form.querySelector('form');
  if (submissionForm instanceof HTMLFormElement) {
    submissionHost(form).dataset.netsuiteSubmitExpected = 'true';
  }

  // Do not set the native disabled property: the vendor reads the button after this click handler.
  setSubmissionPending(form, true, false);
}

// Form orchestration and lifecycle

function configureForm(form) {
  const behaviorNames = form.dataset.netsuiteBehaviors?.split(/\s+/).filter(Boolean) ?? [];

  behaviorNames.forEach((name) => {
    const behavior = FORM_BEHAVIORS[name];
    if (!behavior?.fields) return;

    configureFieldValues(form, behavior.fields);
  });

  configurePhoneRequirement(form);

  if (submissionHost(form) !== form && submissionIsPending(form)) {
    setSubmissionPending(form, true, false);
  }

  configureSubmission(form);

  if (form.dataset.netsuiteDependencyConfigured) return;

  form.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLSelectElement)) return;

    if (
      event.target === form.querySelector(FIELD_SELECTORS.smsOptIn) ||
      event.target === form.querySelector(FIELD_SELECTORS.contactMethod)
    ) {
      configurePhoneRequirement(form);
    }
  });
  form.addEventListener('input', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target !== form.querySelector(FIELD_SELECTORS.phone)) return;

    syncPhoneFormatValidity(event.target);
    if (event.target.checkValidity()) clearFieldError(event.target);
  });
  // Show a format error as soon as the shopper leaves the field, not only on submit.
  form.addEventListener(
    'blur',
    (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target !== form.querySelector(FIELD_SELECTORS.phone)) return;

      syncPhoneFormatValidity(event.target);
      if (event.target.validity.customError) {
        showFieldError(event.target, PHONE_INVALID_MESSAGE, { focus: false });
      }
    },
    true
  );
  form.dataset.netsuiteDependencyConfigured = 'true';
}

function syncStockNotifyVisibility(section, updatedHtml) {
  if (!section || !updatedHtml?.getElementById) return;

  section.querySelectorAll(STOCK_NOTIFY_SELECTOR).forEach((element) => {
    if (!(element instanceof HTMLElement) || !element.id) return;

    const updatedElement = updatedHtml.getElementById(element.id);
    if (updatedElement) element.hidden = updatedElement.hasAttribute('hidden');
  });
}

export function configureNetSuiteForms(root = document) {
  if (root instanceof Element) {
    const form = root.matches(FORM_SELECTOR) ? root : root.closest(FORM_SELECTOR);
    if (form) configureForm(form);
  }

  root.querySelectorAll?.(FORM_SELECTOR).forEach(configureForm);
}

window.addEventListener('click', handleSubmitControlClick, { capture: true });

configureNetSuiteForms();

// The app renders fields asynchronously, so configure every form added to the DOM.
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) configureNetSuiteForms(node);
    });
  });
});

observer.observe(document.documentElement, { childList: true, subtree: true });

// Variant changes replace the product SKU after the app form has rendered.
document.addEventListener('variant:update', (event) => {
  const section = event.target instanceof Element ? event.target.closest('[id*="ProductInformation-"]') : null;
  const sku = event.detail?.resource?.sku;
  const updatedHtml = event.detail?.data?.html;

  if (section && typeof sku === 'string') {
    section.querySelectorAll('[data-product-sku]').forEach((element) => {
      element.dataset.productSku = sku;
    });
  }

  syncStockNotifyVisibility(section, updatedHtml);

  configureNetSuiteForms(section ?? document);
});
